import * as React from "react";

import PropTypes from "prop-types";
import _ from "lodash";
import Msg from "./Msg";
import { NavItem, Nav } from "react-bootstrap";
import NaviCustomerInfo from "./NaviCustomerInfo";
import NavItemLink from "./NavItemLink";
import NaviDefs from "./NaviDefs";
import { trackStructuredEvent } from "../../utils/TrackingUtil";
import { MenuItem, MenuNode } from "./NaviDefs";

// key: react-path-part, value: name for tracking
// TODO (km) check how to integrate this into NaviDefs.js
const _tracknamesOfSourcesNotInNavigationBar = {
    startpage: "startpage",
    "tour-detail": "tour-detail",
    customersearch: "customersearch",
    selectAddress: "address-select",
    printPickingLists: "picklist-print",
    reorder: "reorder"
} as { [k: string]: string };

// lets the content be movable to left and right,
// scrolls the active item into view if it is overflown.
type Props = {
    children?: any;
    className: string;
    activeItemKey: string;
};

class DraggablePane extends React.Component<Props> {
    domElem: Element | undefined | null = null; // will be used to manipulate the dom

    componentDidMount() {
        this.scrollToActiveItem();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.activeItemKey !== this.props.activeItemKey) {
            this.scrollToActiveItem();
        }
    }

    // if the active item is scrolled out of the viewport, it should be made visible
    scrollToActiveItem = () => {
        const domElem = this.domElem;
        if (domElem) {
            const currentItem: any = domElem.querySelector(".active"); // current
            if (currentItem) {
                // check if is overflown on the right
                const bounding = domElem.getBoundingClientRect();
                const right = currentItem.offsetLeft + currentItem.offsetWidth - domElem.scrollLeft;
                const overflowright = right - bounding.width;
                if (overflowright > 0) {
                    domElem.scrollLeft = domElem.scrollLeft + overflowright + 1;
                    return;
                }
                // check it item is already scrolled out on the left
                const left = currentItem.offsetLeft - domElem.scrollLeft;
                if (left < 0) {
                    domElem.scrollLeft = domElem.scrollLeft + left + 1;
                }
            }
        }
    };

    render() {
        return (
            <div
                className={"row-scroll-container " + (this.props.className || "")}
                ref={(domElem) => {
                    this.domElem = domElem;
                }}>
                <div className="row-scroll-pane">{this.props.children}</div>
            </div>
        );
    }
}

/**
 * A ribbon style navigation
 */

type RibbonProps = {
    children?: {
        [x: string]: any;
    };
    showBeta: boolean;
    showOrderFulfillment: boolean;
};

class NaviRibbon extends React.Component<RibbonProps, any> {
    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props: RibbonProps, context: any) {
        super(props, context);
        const topItem = this.topItemOfCurrentPage();
        const currentItem = topItem && topItem.children ? this.subActiveItem(topItem.children) : null;

        this.state = {
            selectedTopItem: topItem as MenuNode | undefined | null /* the top item that is selected */,
            currentSubItem: currentItem as MenuItem | undefined | null
        };
    }

    // the visible page may be changed,
    // then we need to recalculate current item,
    // because the subribbon may need to be changed.
    // As the user can change the top item intentionally, take care to only change the top item when the page has actually changed.
    componentWillReceiveProps() {
        const newTopItem = this.topItemOfCurrentPage();
        const newCurrentItem = newTopItem && newTopItem.children ? this.subActiveItem(newTopItem.children) : null; // TODO (KM): check how we handle active topitem
        if (newCurrentItem !== _.get(this.state, ["currentSubItem"])) {
            if (newTopItem && newTopItem !== _.get(this.state, ["selectedTopItem"])) {
                this.setState({
                    selectedTopItem: newTopItem, // this will change the visible tab and subribbon
                    currentSubItem: newCurrentItem
                });
            } else {
                this.setState({
                    currentSubItem: newCurrentItem
                });
            }
        }
        // else: page has not changed, so do not change anything
    }

    keyForItem = (item: any): string => {
        return "nav-" + (item.trackAs ? item.trackAs : item.text);
    };

    // react key of the active item of the top ribbon
    topActiveKey = (): string => {
        const topItem = _.get(this.state, ["selectedTopItem"]);
        if (topItem) {
            return topItem.top ? this.keyForItem(topItem.top) : this.keyForItem(topItem);
        } else {
            return "";
        }
    };

    subActiveItem = (items: MenuItem[]): MenuItem | undefined | null => {
        for (const item of items) {
            if (this.itemHasActivePage(item)) {
                return item;
            }
        }
        return null;
    };

    subActiveKey = (items: MenuItem[]): string => {
        for (const item of items) {
            if (this.itemHasActivePage(item)) {
                return this.keyForItem(item);
            }
        }
        return "";
    };

    classForItem = (item: MenuItem): string => {
        return item.testId ? "test-" + item.testId : "";
    };

    trackAsForNode = (node: MenuNode | undefined | null): string => {
        if (node) {
            if (node.trackAs) {
                return node.trackAs;
            } else if (node.top && node.top.trackAs) {
                return node.top.trackAs;
            }
        }
        return "unknown";
    };

    currentSubItems = () => {
        // return submenu according to the state
        if (this.state.selectedTopItem) {
            return NaviDefs.filteredSubItems(
                this.state.selectedTopItem,
                this.props.showBeta,
                this.props.showOrderFulfillment
            );
        } else {
            return [];
        }
    };

    // menu item corresponds to that page that is visible.
    itemHasActivePage = (item: MenuItem): boolean => {
        if (this.isActive(item.url, null, item.url === "/")) {
            return true;
        } else if (item.otherUrls) {
            for (const url of item.otherUrls) {
                if (this.isActive(url)) {
                    return true;
                }
            }
        }
        return false;
    };

    hasActiveChild = (node: MenuNode): boolean => {
        if (node.children) {
            for (const item of node.children) {
                if (this.itemHasActivePage(item)) {
                    return true;
                }
            }
        }
        return false;
    };

    /*
     *  find the top item where the currently seen page is a child of
     */
    topItemOfCurrentPage = (): MenuNode | undefined | null => {
        const menuDef = NaviDefs.getHierarchicMenuDef();
        for (const node of menuDef) {
            if (node.children) {
                if (this.hasActiveChild(node)) {
                    return node;
                }
            } else if (node.url && this.itemHasActivePage(node)) {
                return node;
            }
        }
        return null;
    };

    subItemOfCurrentPage = (): MenuItem | undefined | null => {
        const menuDef = NaviDefs.getHierarchicMenuDef();
        for (const node of menuDef) {
            if (node.children) {
                for (const item of node.children) {
                    if (this.itemHasActivePage(item)) {
                        return item;
                    }
                }
            } else if (node.url && this.itemHasActivePage(node)) {
                return node;
            }
        }
        return null;
    };

    // get the menu entry of the currently active page, for tracking purposes
    getActivePageTrackAs = (): string | undefined | null => {
        const topItem = this.topItemOfCurrentPage();
        if (topItem) {
            if (topItem.children) {
                const menuItem = topItem.children.find((entry) => {
                    return this.isActive(entry.url);
                });
                if (menuItem) {
                    return menuItem.trackAs;
                }
            } else {
                return topItem.trackAs;
            }
        }
        const navkey = _.find(Object.keys(_tracknamesOfSourcesNotInNavigationBar), (key: string) => {
            return this.isActive(key);
        });
        if (navkey) {
            return _tracknamesOfSourcesNotInNavigationBar[navkey];
        }
        return "unknown";
    };

    trackMenuClick = (destination: MenuItem) => {
        const sourceName = this.getActivePageTrackAs() || "unknown";
        trackStructuredEvent("employeeNavigation.menuClick", {
            tags: { menuSource: sourceName, menuTarget: destination.trackAs }
        });
    };

    trackRibbonChange = (destination: MenuNode) => {
        const sourceName = this.trackAsForNode(this.state.selectedTopItem);
        const targetName = this.trackAsForNode(destination);
        trackStructuredEvent("employeeNavigation.menuRibbonChange", {
            tags: { menuSource: sourceName, menuTarget: targetName }
        });
    };

    renderSubItem = (item: MenuItem) => {
        const key = this.keyForItem(item);
        let className = this.classForItem(item);
        if (this.itemHasActivePage(item)) {
            className = className += " current";
        }
        let content;
        if (item.iconId) {
            content = <span className={item.iconId} />;
        } else {
            content = <Msg k={item.text} />;
        }
        const marker = item.beta ? "*" : null;
        return (
            <NavItemLink
                key={key}
                to={item.url}
                className={className}
                isOrderFulfillment={item.isOrderFulfillment}
                isNonBackendUrl={item.isNonBackendUrl}
                isLogout={item.isLogout}
                text={item.text}
                onClick={this.trackMenuClick.bind(this, item)}
                disabled={false}>
                {content}
                {marker}
            </NavItemLink>
        );
    };

    needsExtraPanel = (forInfo: string): boolean => {
        return (
            !!this.state.selectedTopItem &&
            !!this.state.selectedTopItem.extraInfo &&
            this.state.selectedTopItem.extraInfo === forInfo
        );
    };

    currentPageIsInActiveRibbon = (): boolean => {
        const topItem = this.state.selectedTopItem;
        if (topItem && this.hasActiveChild(topItem)) {
            return true;
        }
        return false;
    };

    customerSelectEnabled = (): boolean => {
        const item = this.subItemOfCurrentPage();
        return !!item && !item.hasOwnCustomerSelect;
    };

    renderExtraInfo = () => {
        if (this.needsExtraPanel("customer")) {
            const enabled = this.customerSelectEnabled();
            return (
                <NaviCustomerInfo
                    // @ts-ignore
                    selectRedirectsBack={this.currentPageIsInActiveRibbon()}
                    selectEnabled={enabled}
                />
            );
        } else {
            return <span />;
        }
    };

    renderSubRibbons = (items: MenuItem[]) => {
        const activeItemKey = this.subActiveKey(items);
        return (
            <DraggablePane className={"subRibbon"} activeItemKey={activeItemKey}>
                {this.renderExtraInfo()}
                <Nav bsStyle="pills">{items.map(this.renderSubItem)}</Nav>
            </DraggablePane>
        );
    };

    handleTopItemSelect = (node: MenuNode) => {
        this.trackRibbonChange(node); // need to be first to access the current selected top item
        this.setState({
            selectedTopItem: node
        });
    };

    renderTopPlainItem = (item: MenuItem) => {
        const key = this.keyForItem(item);
        const className = this.classForItem(item) + (this.isActive(item.url) ? " current" : "");
        let content;
        if (item.iconId) {
            content = <span className={item.iconId} />;
        } else {
            content = <Msg k={item.text} />;
        }
        return (
            <NavItemLink
                key={key}
                to={item.url}
                className={className}
                disabled={false}
                isOrderFulfillment={item.isOrderFulfillment}
                isNonBackendUrl={item.isNonBackendUrl}
                onClick={this.handleTopItemSelect.bind(this, item)}
                active={item === this.state.selectedTopItem}>
                {content}
            </NavItemLink>
        );
    };

    renderTopItem = (item: MenuItem, node: MenuNode) => {
        const key = this.keyForItem(item);
        const className = this.classForItem(item) + (this.hasActiveChild(node) ? " current" : "");
        const content = item.iconId ? <span className={item.iconId} /> : <Msg k={item.text} />;
        return (
            <NavItem
                key={key}
                href={item.url}
                className={className}
                onSelect={this.handleTopItemSelect.bind(this, node)}
                active={node === this.state.selectedTopItem}>
                {content}
            </NavItem>
        );
    };

    renderTopRibbons = (menuDef: Array<MenuNode>) => {
        const activeItemKey = this.topActiveKey();
        return (
            <DraggablePane className="topRibbon ofHeaderEmployeeNavLinks" activeItemKey={activeItemKey}>
                <Nav bsStyle="tabs" activeKey={activeItemKey}>
                    {_.map(menuDef, (item) => {
                        if (item.top) {
                            return this.renderTopItem(item.top, item);
                        } else {
                            return this.renderTopPlainItem(item);
                        }
                    })}
                </Nav>
            </DraggablePane>
        );
    };

    isActive = (
        pathname: string | undefined | null,
        query?: string | undefined | null,
        indexOnly?: boolean | undefined | null
    ): boolean => {
        return this.context.router.isActive({ pathname, query }, indexOnly);
    };

    render() {
        const menuDef = NaviDefs.filteredTopItems(
            NaviDefs.getHierarchicMenuDef(),
            this.props.showBeta,
            this.props.showOrderFulfillment
        );
        return (
            <div className="bty-header-employee-ribbon test-employee-navbar navbar-fixed-top">
                {this.renderTopRibbons(menuDef)}
                {this.renderSubRibbons(this.currentSubItems())}
            </div>
        );
    }
}

export default NaviRibbon;
